import { useEffect, useRef, useState } from 'react';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FreteOpcao } from '@/api/endpoints/orcamentos';
import { brl } from '../calc';
import { Field, StepHeader } from '../ui';
import { inputCls, linkBtnCls, nf } from '../styles';
import { formatCep, useViaCep } from '../useViaCep';

type Origem = { cep: string; local: string };

/**
 * Cota sozinho ao abrir o passo (se já tem CEP e kit) e deixa escolher entre
 * as opções. Valor manual só aparece quando pedirem ou quando a cotação falha.
 */
export function FreteStep({
  origem,
  onOrigemChange,
  cep,
  local,
  pesoKg,
  podeCotar,
  opcoes,
  configurado,
  cotando,
  erro,
  valor,
  onCotar,
  onValor,
}: {
  origem: Origem;
  onOrigemChange: (o: Origem) => void;
  cep: string;
  local: string;
  pesoKg: number;
  podeCotar: boolean;
  opcoes: FreteOpcao[];
  configurado: boolean | null;
  cotando: boolean;
  erro: boolean;
  valor: number;
  onCotar: () => void;
  onValor: (v: number) => void;
}) {
  const [manual, setManual] = useState(false);
  const autoTried = useRef(false);

  useEffect(() => {
    if (autoTried.current || !podeCotar || opcoes.length > 0 || cotando) return;
    autoTried.current = true;
    onCotar();
  }, [podeCotar, opcoes.length, cotando, onCotar]);

  const semCotacao = configurado === false || erro;
  const mostrarManual = manual || semCotacao || !podeCotar;

  return (
    <div>
      <StepHeader
        title="Frete até o cliente"
        description={`Kit com ${pesoKg.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} kg${local ? `, entrega em ${local}` : ''}.`}
      />

      <OrigemFrete origem={origem} onChange={onOrigemChange} />

      {!podeCotar && (
        <p className="mb-4 text-sm text-text-secondary">
          {cep ? 'Adicione itens ao kit' : 'Informe o CEP no passo Cliente'} pra cotar
          automaticamente.
        </p>
      )}

      {cotando && (
        <p className="flex items-center gap-2 py-3 text-sm text-text-secondary">
          <Loader2 size={16} className="animate-spin" /> Cotando no Melhor Envio…
        </p>
      )}

      {configurado === false && (
        <p className="mb-4 flex items-start gap-2 text-sm text-text-secondary">
          <AlertCircle size={16} className="mt-0.5 shrink-0 text-warning" />
          Cotação automática desligada no servidor (falta o token do Melhor Envio). Digite o valor
          do frete.
        </p>
      )}
      {erro && (
        <p className="mb-4 flex items-start gap-2 text-sm text-text-secondary">
          <AlertCircle size={16} className="mt-0.5 shrink-0 text-error" />
          Não deu pra cotar. Confira o CEP ou digite o valor.
        </p>
      )}

      {opcoes.length > 0 && (
        <div className="flex flex-col gap-2" role="radiogroup" aria-label="Opções de frete">
          {opcoes.map((o) => {
            const checked = !manual && valor === o.preco;
            return (
              <button
                key={o.id}
                type="button"
                role="radio"
                aria-checked={checked}
                onClick={() => {
                  setManual(false);
                  onValor(o.preco);
                }}
                className={cn(
                  'flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors',
                  checked
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-muted hover:border-border-strong'
                )}
              >
                <span
                  className={cn(
                    'flex size-4 shrink-0 items-center justify-center rounded-full border',
                    checked ? 'border-primary' : 'border-border-strong'
                  )}
                >
                  {checked && <span className="size-2 animate-pop rounded-full bg-primary" />}
                </span>
                <span className="flex-1 text-sm">
                  <span className="font-medium text-text-title">
                    {o.empresa} {o.nome}
                  </span>
                  {o.prazoDias != null && (
                    <span className="text-text-secondary"> · {o.prazoDias} dias úteis</span>
                  )}
                </span>
                <span className="text-sm font-semibold text-text-title">{brl(o.preco)}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
        {podeCotar && !cotando && (
          <button
            type="button"
            className={cn(linkBtnCls, 'inline-flex items-center gap-1')}
            onClick={onCotar}
          >
            <RefreshCw size={13} /> Cotar de novo
          </button>
        )}
        {!mostrarManual && (
          <button type="button" className={linkBtnCls} onClick={() => setManual(true)}>
            Digitar outro valor
          </button>
        )}
      </div>

      {mostrarManual && (
        <div className="mt-4 max-w-xs">
          <Field
            label="Valor do frete (R$)"
            hint="Use 0 se o cliente retira ou o frete é por conta dele."
          >
            <input
              type="number"
              min={0}
              step="0.01"
              className={inputCls}
              value={valor}
              onChange={(e) => onValor(nf(e.target.value))}
            />
          </Field>
        </div>
      )}
    </div>
  );
}

/** "Saindo de X" em texto; vira campo de CEP só quando a equipe está em outro lugar. */
function OrigemFrete({ origem, onChange }: { origem: Origem; onChange: (o: Origem) => void }) {
  const [editando, setEditando] = useState(false);
  const [cep, setCep] = useState(origem.cep);
  const status = useViaCep(editando ? cep : '', ({ cidade, uf }) => {
    onChange({ cep: formatCep(cep), local: [cidade, uf].filter(Boolean).join(', ') });
    setEditando(false);
  });

  if (!editando) {
    return (
      <p className="mb-5 text-sm text-text-secondary">
        Saindo de <span className="text-text-title">{origem.local}</span> ({origem.cep}).{' '}
        <button
          type="button"
          className={linkBtnCls}
          onClick={() => {
            setCep('');
            setEditando(true);
          }}
        >
          trocar origem
        </button>
      </p>
    );
  }

  return (
    <div className="mb-5 flex flex-wrap items-end gap-3">
      <div className="w-44">
        <Field
          label="CEP de onde sai o frete"
          hint={
            status === 'loading'
              ? 'Buscando cidade…'
              : status === 'notfound'
                ? 'CEP não encontrado.'
                : undefined
          }
        >
          <input
            className={inputCls}
            inputMode="numeric"
            value={cep}
            onChange={(e) => setCep(formatCep(e.target.value))}
            placeholder="00000-000"
            autoFocus
          />
        </Field>
      </div>
      <button type="button" className={cn(linkBtnCls, 'pb-2')} onClick={() => setEditando(false)}>
        cancelar
      </button>
    </div>
  );
}
